import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { contactsAPI } from "../../services/api";
import DataTable from "../../components/DataTable/DataTable";
import "../Apps/Apps.css";
import { useToast } from "../../hooks/useToast";

interface Contact {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  department: string;
  message: string;
  status: "open" | "closed";
  assignedTo?: { name: string } | string;
  createdAt: string;
}

const ContactsList = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    department: "",
    status: "",
  });

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchContacts = async () => {
    try {
      setIsLoading(true);
      const params: any = { limit: 1000 };
      if (filters.department) params.department = filters.department;
      if (filters.status) params.status = filters.status;

      const response = await contactsAPI.getAll(params);
      const data = response.data.data || response.data || [];
      setContacts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (contact: Contact) => {
    navigate(`/admin/contacts/${contact._id}`);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this contact?"))
      return;

    try {
      await contactsAPI.delete(id);
      await fetchContacts();
    } catch (error) {
      console.error("Failed to delete contact:", error);
      showToast({
        type:"error",
        title:"Delete failed",
        message:
        "Failed to delete contact"
      });
    }
  };

  const getAssignedName = (assignedTo?: { name: string } | string) => {
    if (!assignedTo) return "-";
    if (typeof assignedTo === "string") return assignedTo;
    return assignedTo?.name || "-";
  };

  const columns = [
    {
      key: "name",
      label: "Contact",
      render: (contact: Contact) => (
        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
            {contact.name}
          </div>
          <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
            {contact.email}
          </div>
        </div>
      ),
    },
    {
      key: "subject",
      label: "Subject",
      render: (contact: Contact) => (
        <div
          style={{
            maxWidth: "250px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {contact.subject}
        </div>
      ),
    },
    {
      key: "department",
      label: "Department",
      render: (contact: Contact) => (
        <span style={{ textTransform: "capitalize" }}>
          {contact.department}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (contact: Contact) => (
        <span className={`status-badge ${contact.status}`}>
          {contact.status}
        </span>
      ),
    },
    {
      key: "assignedTo",
      label: "Assigned To",
      render: (contact: Contact) => getAssignedName(contact.assignedTo),
    },
    {
      key: "createdAt",
      label: "Date",
      render: (contact: Contact) => (
        <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
          {new Date(contact.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ];

  return (
    <div className="contacts-page">
      <div className="page-header">
        <div>
          <h1>Contacts Management</h1>
          <p>View and manage contact inquiries</p>
        </div>
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <label>Department</label>
          <select
            value={filters.department}
            onChange={(e) =>
              setFilters({ ...filters, department: e.target.value })
            }
          >
            <option value="">All Departments</option>
            <option value="general">General</option>
            <option value="investors">Investors</option>
            <option value="technology">Technology</option>
            <option value="careers">Careers</option>
            <option value="partnerships">Partnerships</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <DataTable
        data={contacts}
        columns={columns}
        isLoading={isLoading}
        onEdit={handleView}
        onDelete={handleDelete}
        emptyMessage="No contacts found"
        editLabel="View Details"
      />
    </div>
  );
};

export default ContactsList;
